import { Box, Card, Typography, Grid, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getChartColors } from '../utils/chartConfig';

/**
 * CFORecommendationSection - CFO Decision Framework
 *
 * Shows approval recommendation, decision rationale, and strategic fit score.
 * Matches Print View Page 7 content.
 */
export default function CFORecommendationSection({ roiResults, cfoMetrics, companyConfig }) {
  const theme = useTheme();
  const colors = getChartColors(theme.palette.mode);
  const summary = roiResults?.portfolio?.summary || {};
  const tier4 = cfoMetrics?.tier4_capital || {};

  // Calculate analysis values
  const capex = summary.total_treatment_cost || 0;
  const energyOnlySavings = summary.total_annual_savings || 0;
  const totalAnnualValue = summary.total_annual_value || energyOnlySavings;
  const discountRate = companyConfig?.discountRate || 8;

  // IRR calculation
  const irrLow = capex > 0 ? ((energyOnlySavings / capex) * 100) : 0;
  const irrHigh = capex > 0 ? ((totalAnnualValue / capex) * 100) : 0;

  // Break-even threshold
  const breakEvenThresholdPct = capex > 0 && totalAnnualValue > 0
    ? Math.round((capex / (totalAnnualValue * 5)) * 100)
    : 20;

  // True cash breakeven months
  const trueCashBreakevenMonths = totalAnnualValue > 0
    ? (capex / totalAnnualValue) * 12
    : 0;

  const rationaleItems = [
    `IRR ${irrLow.toFixed(0)}-${irrHigh.toFixed(0)}% exceeds ${discountRate}% WACC by ${Math.round(irrLow / discountRate)}×`,
    `True cash breakeven in ${trueCashBreakevenMonths.toFixed(1)} months using verified energy savings only`,
    `Validated: Returns positive even at ${breakEvenThresholdPct}% of documented averages`,
    `${trueCashBreakevenMonths.toFixed(0)}-month capital-at-risk window (upfront)`,
    'Low execution risk: No operational disruption, reversible, proven technology',
  ];

  const scoreItems = [
    { label: 'Margin Improvement', score: '5/5' },
    { label: 'Operational Resilience', score: '5/5' },
    { label: 'Scalability', score: '5/5' },
    { label: 'Execution Simplicity', score: '5/5' },
  ];

  return (
    <Box sx={{ mt: 4, mb: 3 }}>
      {/* Section Header */}
      <Typography
        variant="h6"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          fontSize: '1.1rem',
          color: 'text.primary',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        CFO Recommendation
      </Typography>

      {/* Approval Badge */}
      <Card
        sx={{
          p: 2,
          mb: 3,
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%)',
          border: '2px solid #4CAF50',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
        }}
      >
        <CheckCircleIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.secondary', fontSize: '1.5rem' }} />
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            fontSize: '1rem',
            color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
          }}
        >
          Approval Recommended — Decision Grade
        </Typography>
      </Card>

      <Grid container spacing={3}>
        {/* Decision Rationale */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              p: 2.5,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              height: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: '0.8rem',
                color: 'text.secondary',
                mb: 2,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Decision-Grade Rationale
            </Typography>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {rationaleItems.map((item, idx) => (
                <Box
                  key={idx}
                  component="li"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1.5,
                    '&:last-child': { mb: 0 },
                  }}
                >
                  <CheckCircleIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.secondary', fontSize: '0.9rem', mt: 0.25, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
                      lineHeight: 1.5,
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>

        {/* Strategic Fit Score */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              p: 2.5,
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.03) 100%)',
              border: '1px solid rgba(76, 175, 80, 0.2)',
              borderRadius: 1.5,
              height: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontWeight: 600,
                fontSize: '0.7rem',
                color: 'text.secondary',
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Strategic Fit Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
                  lineHeight: 1,
                }}
              >
                5
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '1.2rem',
                  fontWeight: 500,
                  color: 'text.disabled',
                }}
              >
                /5
              </Typography>
            </Box>
            <Box
              sx={{
                pt: 1.5,
                borderTop: '1px solid rgba(76, 175, 80, 0.2)',
              }}
            >
              {scoreItems.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 600,
                      color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary',
                    }}
                  >
                    {item.score}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Approval Conditions */}
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: 'text.secondary',
            mb: 1.5,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Approval Conditions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2,
                background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: '3px solid #4CAF50',
                borderRadius: '0 6px 6px 0',
                height: '100%',
              }}
            >
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Performance Guarantee
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                Restore to design efficiency — contractually guaranteed
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2,
                background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: '3px solid #4CAF50',
                borderRadius: '0 6px 6px 0',
                height: '100%',
              }}
            >
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Payment Structure
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                3-year deferred recommended. Upfront only if tax incentives improve after-tax IRR.
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: 2,
                background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: '3px solid #4CAF50',
                borderRadius: '0 6px 6px 0',
                height: '100%',
              }}
            >
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : 'text.primary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Equipment Lifecycle
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                Covered by product guarantee — older systems gain life extension value
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
